import { makeStyles } from "@mui/styles";
import { COLORS } from "../../../constant/colors";

export default makeStyles(() => ({
  // ─── SECTION WRAPPER ───────────────────────────────────────────────
  promoSec: {
    padding: "100px 0",
    position: "relative",
    overflow: "hidden",
    background: `linear-gradient(135deg, ${COLORS.cream} 0%, ${COLORS.creamLight} 100%)`,
    "@media (max-width: 991px)": { padding: "70px 0" },
    "@media (max-width: 600px)": { padding: "50px 0" },
  },

  // ─── BACKGROUND ORBS ───────────────────────────────────────────────
  bgOrb1: {
    position: "absolute",
    top: "-120px",
    right: "-120px",
    width: "500px",
    height: "500px",
    borderRadius: "50%",
    background: `radial-gradient(circle, rgba(113,0,20,0.08) 0%, transparent 70%)`,
    pointerEvents: "none",
  },
  bgOrb2: {
    position: "absolute",
    bottom: "-100px",
    left: "-80px",
    width: "380px",
    height: "380px",
    borderRadius: "50%",
    background: `radial-gradient(circle, rgba(179,148,2,0.08) 0%, transparent 70%)`,
    pointerEvents: "none",
  },
  bgOrb3: {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: "600px",
    height: "600px",
    borderRadius: "50%",
    background: `radial-gradient(circle, rgba(113,0,20,0.04) 0%, transparent 70%)`,
    pointerEvents: "none",
  },
  bgGrid: {
    position: "absolute",
    inset: 0,
    backgroundImage: `
      linear-gradient(rgba(113,0,20,0.03) 1px, transparent 1px),
      linear-gradient(90deg, rgba(113,0,20,0.03) 1px, transparent 1px)
    `,
    backgroundSize: "48px 48px",
    pointerEvents: "none",
  },

  // ─── INNER LAYOUT ──────────────────────────────────────────────────
  innerWrap: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "60px",
    position: "relative",
    zIndex: 2,
    "@media (max-width: 991px)": {
      flexDirection: "column",
      textAlign: "center",
      gap: "40px",
    },
  },

  contentSide: {
    flex: 1,
    maxWidth: "520px",
    "@media (max-width: 991px)": { maxWidth: "100%" },
  },

  imageSide: {
    flex: 1,
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
  },

  // ─── BADGE ─────────────────────────────────────────────────────────
  badgeRow: {
    marginBottom: "20px",
  },
  badge: {
    display: "inline-flex",
    alignItems: "center",
    gap: "8px",
    background: "rgba(113,0,20,0.1)",
    border: `1px solid rgba(113,0,20,0.2)`,
    color: COLORS.primary,
    padding: "7px 18px",
    borderRadius: "100px",
    fontSize: "0.78rem",
    fontWeight: 600,
    letterSpacing: "1.5px",
    textTransform: "uppercase",
    backdropFilter: "blur(8px)",
    animation: "$pulseBadge 2.5s ease-in-out infinite",
  },
  badgeDot: {
    width: "7px",
    height: "7px",
    borderRadius: "50%",
    background: COLORS.primary,
    boxShadow: `0 0 8px ${COLORS.primary}`,
    animation: "$blinkDot 1.4s ease-in-out infinite",
  },
  "@keyframes pulseBadge": {
    "0%, 100%": { boxShadow: `0 0 0 0 rgba(113,0,20,0.2)` },
    "50%": { boxShadow: `0 0 0 8px rgba(113,0,20,0)` },
  },
  "@keyframes blinkDot": {
    "0%, 100%": { opacity: 1 },
    "50%": { opacity: 0.2 },
  },

  // ─── HEADING ───────────────────────────────────────────────────────
  heading: {
    fontSize: "clamp(2.4rem, 4.5vw, 3.6rem)",
    fontWeight: 800,
    color: COLORS.primary,
    lineHeight: 1.12,
    marginBottom: "18px",
    fontFamily: "'Playfair Display', Georgia, serif",
    letterSpacing: "-0.5px",
    "@media (max-width: 600px)": { fontSize: "2rem" },
  },
  headingAccent: {
    background: `linear-gradient(90deg, ${COLORS.primary}, ${COLORS.secondary})`,
    WebkitBackgroundClip: "text",
    WebkitTextFillColor: "transparent",
    backgroundClip: "text",
    fontStyle: "italic",
  },

  subtext: {
    fontSize: "1.05rem",
    color: COLORS.textSecondary,
    lineHeight: 1.75,
    marginBottom: "32px",
    fontFamily: "'DM Sans', sans-serif",
    "@media (max-width: 991px)": { maxWidth: "480px", margin: "0 auto 32px" },
  },

  // ─── BULLET LIST ───────────────────────────────────────────────────
  bulletList: {
    listStyle: "none",
    padding: 0,
    margin: "0 0 40px 0",
    display: "flex",
    flexDirection: "column",
    gap: "14px",
    "@media (max-width: 991px)": {
      display: "inline-flex",
      textAlign: "left",
    },
  },
  bulletItem: {
    display: "flex",
    alignItems: "center",
    gap: "14px",
    fontSize: "1rem",
    color: COLORS.textPrimary,
    fontFamily: "'DM Sans', sans-serif",
    "@media (max-width: 600px)": { fontSize: "0.92rem" },
  },
  bulletIcon: {
    width: "28px",
    height: "28px",
    borderRadius: "50%",
    background: `linear-gradient(135deg, ${COLORS.primary}, ${COLORS.coralDark})`,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
    boxShadow: `0 4px 12px rgba(113,0,20,0.2)`,
  },

  // ─── CTA ───────────────────────────────────────────────────────────
  ctaRow: {
    display: "flex",
    alignItems: "center",
    gap: "20px",
    flexWrap: "wrap",
    "@media (max-width: 991px)": { justifyContent: "center" },
  },
  ctaButton: {
    display: "inline-flex",
    alignItems: "center",
    gap: "12px",
    background: `linear-gradient(135deg, ${COLORS.primary} 0%, ${COLORS.coralDark} 100%)`,
    color: "#fff",
    padding: "15px 36px",
    borderRadius: "100px",
    fontSize: "1rem",
    fontWeight: 600,
    fontFamily: "'DM Sans', sans-serif",
    textDecoration: "none",
    transition: "transform 0.25s ease, box-shadow 0.25s ease",
    boxShadow: `0 12px 30px rgba(113,0,20,0.2)`,
    position: "relative",
    overflow: "hidden",
    "&>span": {
      position: "relative",
      zIndex: 10,
    },
    "&::before": {
      content: '""',
      position: "absolute",
      inset: 0,
      background: `linear-gradient(135deg, ${COLORS.coralDark} 0%, ${COLORS.primary} 100%)`,
      opacity: 0,
      transition: "opacity 0.3s",
    },
    "&:hover": {
      transform: "translateY(-3px)",
      boxShadow: `0 18px 40px rgba(113,0,20,0.3)`,
      color: "#fff",
      textDecoration: "none",
    },
    "&:hover::before": { opacity: 1 },
    "&:hover $ctaArrow": { transform: "translateX(5px)" },
    "@media (max-width: 600px)": { padding: "13px 28px", fontSize: "0.95rem" },
  },
  ctaArrow: {
    display: "flex",
    alignItems: "center",
    position: "relative",
    zIndex: 1,
    transition: "transform 0.25s ease",
    "& > *": { position: "relative", zIndex: 1 },
  },
  ctaNote: {
    fontSize: "0.85rem",
    color: COLORS.textSecondary,
    fontFamily: "'DM Sans', sans-serif",
  },

  // ─── IMAGE SIDE ────────────────────────────────────────────────────
  imageFrame: {
    position: "relative",
    width: "100%",
    maxWidth: "460px",
  },
  imageGlow: {
    position: "absolute",
    inset: "-30px",
    background: `radial-gradient(circle at 50% 60%, rgba(113,0,20,0.1) 0%, transparent 70%)`,
    borderRadius: "50%",
    zIndex: 0,
    animation: "$glowPulse 3s ease-in-out infinite",
  },
  "@keyframes glowPulse": {
    "0%, 100%": { opacity: 0.6, transform: "scale(1)" },
    "50%": { opacity: 1, transform: "scale(1.05)" },
  },
  imageRing: {
    position: "absolute",
    inset: "-16px",
    borderRadius: "28px",
    border: `1px solid rgba(113,0,20,0.1)`,
    zIndex: 0,
  },
  cakeImg: {
    position: "relative",
    zIndex: 1,
    width: "100%",
    height: "auto",
    borderRadius: "24px",
    boxShadow: "0 30px 60px rgba(0,0,0,0.1), 0 0 0 1px rgba(0,0,0,0.05)",
    transition: "transform 0.5s ease",
    display: "block",
    "&:hover": { transform: "scale(1.025) translateY(-4px)" },
    "@media (max-width: 991px)": { maxWidth: "85%", margin: "0 auto" },
  },

  // ─── FLOATING TAGS ─────────────────────────────────────────────────
  floatTag1: {
    position: "absolute",
    top: "16px",
    left: "-20px",
    background: "rgba(255,255,255,0.9)",
    border: `1px solid rgba(113,0,20,0.1)`,
    backdropFilter: "blur(12px)",
    color: COLORS.textPrimary,
    padding: "10px 16px",
    borderRadius: "14px",
    fontSize: "0.8rem",
    fontWeight: 600,
    fontFamily: "'DM Sans', sans-serif",
    display: "flex",
    alignItems: "center",
    gap: "8px",
    zIndex: 3,
    boxShadow: "0 8px 24px rgba(0,0,0,0.08)",
    animation: "$floatA 4s ease-in-out infinite",
    "& span": { fontSize: "1.1rem" },
    "@media (max-width: 991px)": { left: "5%" },
  },
  floatTag2: {
    position: "absolute",
    bottom: "24px",
    right: "-16px",
    background: "rgba(255,255,255,0.9)",
    border: `1px solid rgba(179,148,2,0.2)`,
    backdropFilter: "blur(12px)",
    color: COLORS.textPrimary,
    padding: "10px 16px",
    borderRadius: "14px",
    fontSize: "0.8rem",
    fontWeight: 600,
    fontFamily: "'DM Sans', sans-serif",
    display: "flex",
    alignItems: "center",
    gap: "8px",
    zIndex: 3,
    boxShadow: "0 8px 24px rgba(0,0,0,0.08)",
    animation: "$floatB 4.5s ease-in-out infinite",
    "& span": { fontSize: "1.1rem" },
    "@media (max-width: 991px)": { right: "5%" },
  },
  "@keyframes floatA": {
    "0%, 100%": { transform: "translateY(0)" },
    "50%": { transform: "translateY(-10px)" },
  },
  "@keyframes floatB": {
    "0%, 100%": { transform: "translateY(0)" },
    "50%": { transform: "translateY(-8px)" },
  },
}));
